import { useState, useEffect, useCallback, useRef } from 'react';
import type React from 'react';

export interface Option {
  label: string;
  value: string;
  otherOption?: string;
}

interface FetchMoreOptions<TData, TVariables> {
  variables: TVariables;
  updateQuery: (
    previousResult: TData,
    result: { fetchMoreResult: TData | null | undefined }
  ) => TData;
}

interface UseInfiniteScrollFilterConfig<
  TData,
  TVariables = Record<string, unknown>
> {
  data: TData | undefined;
  loading: boolean;
  hasNextPage: boolean;
  fetchMore:
    | ((options: FetchMoreOptions<TData, TVariables>) => void)
    | undefined;
  transformData: (data: TData | undefined) => Option[];
  searchTerm: string;
  pageSize?: number;
  selectedValues?: string[];
  buildFetchMoreVariables: (
    nextPage: number,
    searchTerm: string,
    pageSize: number
  ) => TVariables;
}

export function useInfiniteScrollFilter<
  TData = unknown,
  TVariables = Record<string, unknown>
>(config: UseInfiniteScrollFilterConfig<TData, TVariables>) {
  const {
    data,
    loading,
    hasNextPage,
    fetchMore,
    transformData,
    searchTerm,
    pageSize = 50,
    buildFetchMoreVariables,
  } = config;

  const [accumulatedOptions, setAccumulatedOptions] = useState<Option[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isPending, setIsPending] = useState(false);

  // All mutable scroll-state in refs so handleScroll never needs to be recreated.
  // Re-creating handleScroll on every page/hasNextPage change was the root cause of
  // duplicate fetches: the new callback captured stale loadingMoreRef=false before
  // Apollo had a chance to set it back, allowing rapid scrolls to slip through the guard.
  const pageRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const hasNextPageRef = useRef(hasNextPage);
  const loadingRef = useRef(loading);
  const searchTermRef = useRef(searchTerm);
  const fetchMoreRef = useRef(fetchMore);
  const buildFetchMoreVariablesRef = useRef(buildFetchMoreVariables);
  const transformDataRef = useRef(transformData);
  const prevSearchTermRef = useRef(searchTerm);

  // Keep all refs in sync with latest values every render
  useEffect(() => {
    hasNextPageRef.current = hasNextPage;
    loadingRef.current = loading;
    searchTermRef.current = searchTerm;
    fetchMoreRef.current = fetchMore;
    buildFetchMoreVariablesRef.current = buildFetchMoreVariables;
    transformDataRef.current = transformData;
  });

  // Reset when search term changes
  useEffect(() => {
    if (searchTerm !== prevSearchTermRef.current) {
      prevSearchTermRef.current = searchTerm;
      pageRef.current = 0;
      loadingMoreRef.current = false;
      setLoadingMore(false);
      setAccumulatedOptions([]);
      setIsPending(true);
    }
  }, [searchTerm]);

  // Accumulate pages — only fires when Apollo returns new data
  useEffect(() => {
    if (data) {
      setIsPending(false);
      const newOptions = transformDataRef.current(data);
      setAccumulatedOptions((prev) => {
        const existingUuids = new Set(prev.map((opt) => opt.value));
        const uniqueNew = newOptions.filter(
          (opt) => !existingUuids.has(opt.value)
        );
        return [...prev, ...uniqueNew];
      });
      setLoadingMore(false);
      // Release lock only after the browser has painted the new items into the
      // scroll container. Without this delay, the container hasn't expanded yet
      // so scrollBottom is still < 50 and the very next scroll event slips
      // through and fires another fetch before the lock can block it.
      // Two rAF calls: first waits for React commit, second for browser paint.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          loadingMoreRef.current = false;
        });
      });
    }
  }, [data]);

  // Stable scroll handler — never recreated, reads everything from refs
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const scrollBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight;

      if (
        scrollBottom >= 0 &&
        scrollBottom < 20 &&
        hasNextPageRef.current &&
        !loadingMoreRef.current &&
        !loadingRef.current
      ) {
        loadingMoreRef.current = true;
        setLoadingMore(true);
        const nextPage = pageRef.current + 1;
        pageRef.current = nextPage;

        const fm = fetchMoreRef.current;
        if (fm) {
          const vars = buildFetchMoreVariablesRef.current(
            nextPage,
            searchTermRef.current,
            pageSize
          );
          fm({
            variables: vars,
            updateQuery: (previousResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return previousResult;
              return fetchMoreResult;
            },
          });
        }
      }
    },
    [pageSize] // pageSize is the only truly stable external value needed
  );

  return {
    options: accumulatedOptions,
    loadingMore,
    isPending,
    handleScroll,
  };
}
